import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Timer, Bug, Apple, Sparkles, Trophy } from "lucide-react";
import { BrushTimer } from "@/components/fun/BrushTimer";
import { ToothQuiz } from "@/components/fun/ToothQuiz";
import { SugarBugSmash } from "@/components/fun/SugarBugSmash";
import { SnackSorter } from "@/components/fun/SnackSorter";
import { ScrubTooth } from "@/components/fun/ScrubTooth";
import { ToothMascot } from "@/components/site/ToothMascot";

const GAMES = [
  { id: "brush", label: "Brushing Challenge", learn: "Brush 2 minutes, every zone", icon: Timer, C: BrushTimer, bg: "bg-[#ebfbfa]" },
  { id: "bugs", label: "Sugar Bug Smash", learn: "What causes cavities", icon: Bug, C: SugarBugSmash, bg: "bg-[#fff1f1]" },
  { id: "snacks", label: "Snack Sorter", learn: "Tooth-friendly foods", icon: Apple, C: SnackSorter, bg: "bg-[#e0f2fe]" },
  { id: "scrub", label: "Scrub-a-Tooth", learn: "How to brush properly", icon: Sparkles, C: ScrubTooth, bg: "bg-[#fef7e5]" },
  { id: "quiz", label: "Tooth Hero Quiz", learn: "Test what you know", icon: Trophy, C: ToothQuiz, bg: "bg-[#f3f0ff]" },
];

const Arcade = () => {
  const [params] = useSearchParams();
  const [game, setGame] = useState(() => (GAMES.some((g) => g.id === params.get("game")) ? params.get("game") : "brush"));
  const G = GAMES.find((g) => g.id === game);
  return (
    <section className="mx-auto max-w-7xl px-6 pb-10 lg:px-8" data-testid="games-arcade">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {GAMES.map((g) => (
          <button key={g.id} onClick={() => setGame(g.id)} data-testid={`game-pick-${g.id}`}
            className={`btn-pop rounded-[1.5rem] border-[3px] p-4 text-left ${g.bg} ${game === g.id ? "border-slate-800 shadow-[4px_4px_0_#1e293b]" : "border-transparent"}`}>
            <g.icon className="h-7 w-7 text-teal-700" />
            <p className="font-display mt-2 text-base font-semibold text-slate-800">{g.label}</p>
            <p className="text-xs font-semibold text-slate-500">Learn: {g.learn}</p>
          </button>
        ))}
      </div>
      <div className="mx-auto mt-6 max-w-3xl"><G.C key={G.id} /></div>
    </section>
  );
};

const FACTS = [
  ["Tooth enamel is...", "the hardest part of your whole body!"],
  ["Sharks can grow...", "thousands of teeth in their lifetime."],
  ["Your first teeth...", "start forming before you are even born."],
  ["Snails have...", "thousands of tiny teeth on their tongue!"],
];

const FactCard = ({ f, i }) => {
  const [flip, setFlip] = useState(false);
  return (
    <button onClick={() => setFlip(!flip)} data-testid={`fact-card-${i}`}
      className={`btn-pop font-display min-h-[150px] rounded-[1.75rem] border-[3px] border-slate-800 p-6 text-left text-xl font-semibold ${flip ? "bg-[#f5b82e] text-slate-900" : "bg-white text-slate-800"}`}>
      {flip ? f[1] : f[0]}
      <span className="mt-3 block text-xs font-bold uppercase tracking-widest text-slate-500">{flip ? "Tap to flip back" : "Tap to find out"}</span>
    </button>
  );
};

export default function FunZone() {
  return (
    <div className="dot-bg" data-testid="funzone-page">
      <section className="mx-auto max-w-7xl px-6 pb-8 pt-14 lg:px-8">
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <ToothMascot className="animate-floaty w-24" />
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#ff6b6b]">Kids Fun Zone</span>
            <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">Brush, play & be a Tooth Hero!</h1>
          </div>
        </div>
      </section>
      <Arcade />
      <section className="mx-auto max-w-7xl px-6 pb-10 lg:px-8">
        <h2 className="text-2xl font-bold sm:text-3xl">Wow! Tooth facts</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{FACTS.map((f, i) => <FactCard key={i} f={f} i={i} />)}</div>
      </section>
    </div>
  );
}
